import type { LensCardType, LensSpec } from '~/types/leLens';
import lensContract from '~/types/lensContract.json';

export const LENS_DOCUMENT_VERSION = 2;

export type MeasureFormat = 'aud' | 'usd' | 'int' | 'num1' | 'num2' | 'pct' | 'days' | 'nights';
export type Grain = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
export type Compare = 'prior' | 'ly' | 'l2y' | { id: string; vs: string; where: Record<string, Array<string>> };

export type KpiItem = { kpi: string; w?: number; size?: 'lg'; target?: number | string; forecast?: string; against?: Array<{ kpi: string; label?: string }>; label?: string; spark?: false; status?: 'none' | 'border'; numbers?: NumberStyle } & ShownChange;

export type Block = {
  windows?: Array<{ label: string; preset: string; compare?: Array<'prior' | 'ly' | 'l2y'> | 'none' }>;
  id: string;
  title?: string;
  ask?: string;
  askScope?: LensState;
  w?: number;
  h?: number;
  select?: boolean | string;
  by?: Array<string>;
  grain?: Grain;
  window?: string;
  compare?: Array<Compare> | 'none';
  spark?: number;
  top?: number;
  tone?: 'info' | 'good' | 'warning' | 'critical';
  frame?: 'none' | 'hairline' | 'tile';
  status?: 'border' | 'none';
  numbers?: NumberStyle;
  [kind: string]: unknown;
} & ShownChange;

export type DeltaStyle = 'pct' | 'diff' | 'both' | 'none';
export type NumberStyle = 'compact' | 'full';
export type ShownChange = { delta?: DeltaStyle; baseline?: boolean };

export type Column = Array<Block>;
export type RowItem = Block | Column;
export type Row = Array<RowItem>;
export type Section = { id: string; section: string; lead?: string; rows: Array<Row> };
export type Tab = { id: string; tab: string; page: Array<PageEntry> };
export type Tabs = { id: string; tabs: Array<Tab> };
export type PageEntry = Row | Section | Tabs;

export type NumberRange = { min?: number; max?: number };

export type Condition = Array<string> | { not: Array<string> } | NumberRange;

export type Filter = { column: string; style?: 'chip' | 'segmented' | 'select' | 'range'; label?: string; value?: Array<string>; range?: NumberRange; multiple?: false };
export type SqlParam = { sql: string; options: Array<string>; label?: string; multiple?: boolean; value?: Array<string>; role?: 'measure' | 'dimension' | 'grain' };
export type Param =
  | { measures: Array<string>; label?: string }
  | { columns: Array<string>; label?: string }
  | SqlParam
  | { value: number; min?: number; max?: number; step?: number; label?: string };

export type LensDocument = {
  version: typeof LENS_DOCUMENT_VERSION;
  title: string;
  lead?: string;
  audience: 'internal' | 'partner';
  datasets: Array<{ id: string; catalogue?: string; sql?: string }>;
  measures: Record<string, { label?: string; format?: MeasureFormat }>;
  period: { default: string; presets: Array<string>; complete?: Grain; compare: Array<Compare> } & ShownChange;
  filters: Array<Filter>;
  params: Record<string, Param>;
  queries: Record<string, unknown>;
  page: Array<PageEntry>;
  views: Record<string, LensView>;
  display: {
    density?: 'compact' | 'comfortable';
    tone?: 'plain' | 'accent';
    eyebrow?: string;
    frame?: 'none' | 'hairline' | 'tile';
    numbers?: NumberStyle;
    filters?: 'auto' | 'top' | 'side';
    freshness?: 'auto' | 'date' | 'time' | 'hidden';
    other?: 'group' | 'all';
    title?: 'shown' | 'hidden';
    textSize?: 'small' | 'medium' | 'large' | number;
    board?: 'blocks' | 'html';
    view?: string;
  };
};

export type WindowOut = { id: string; from: string; to: string; label: string; weeks?: string };

export type LensViewCard = { cardId: string; query: string; title: string; columns: Array<string> };

export type LensView = { html: string; css?: string; js?: string; bridge?: 'cards'; height?: number; cards?: Array<LensViewCard> };

export type EnvelopeColumn = {
  name: string;
  role: 'dim' | 'time' | 'measure';
  label: string;
  format?: MeasureFormat;
  additive?: false;
  certified?: boolean;
  lowerIsBetter?: boolean;
  definition?: string;
  dataset?: string;
  minN?: number;
  decimals?: number;
};

export type EnvelopeRow = Record<string, string | number | boolean | null>;

export type Envelope = {
  version: 1;
  query: string;
  key?: string;
  status: 'ready' | 'error';
  error?: { code: string; message: string; remedy?: string };
  refreshedAt: string;
  loadedThrough?: string;
  latestAt?: string;
  completeThrough?: string;
  bytes?: number;
  cached?: boolean;
  period: {
    preset: string;
    label: string;
    grain?: Grain;
    current: WindowOut;
    comparisons: Array<WindowOut>;
    partial: boolean;
  };
  reach: Array<string>;
  columns: Array<EnvelopeColumn>;
  rows: Array<EnvelopeRow>;
  compare: Record<string, Array<EnvelopeRow>>;
  totals?: Record<string, EnvelopeRow>;
  spark?: Record<string, Array<{ from: string; to: string; v: number | null }>>;
  notices: Array<string>;
};

export type BlockFamily = 'figures' | 'time' | 'breakdown' | 'bridge' | 'table' | 'prose' | 'none';

export type PlannedBlock = {
  kind?: string;
  family?: BlockFamily;
  view?: string;
  name?: string;
  subtitle?: string;
  query?: string;
  queries?: Array<string>;
  problems?: Array<{ code: string; message: string }>;
  fold?: { param: string; measures: Array<string> };
};

export type LensDocumentData = {
  lensId: string;
  lensVersion: number;
  period: {
    preset: string;
    label: string;
    current: WindowOut;
    comparisons: Array<WindowOut>;
    partial: boolean;
    presets: Array<string>;
  };
  params: Record<string, string | number>;
  blocks: Record<string, PlannedBlock>;
  lead?: Array<string>;
  options?: Record<string, Array<string>>;
  queries: Record<string, Envelope>;
  keys?: Record<string, string>;
  landing?: { through: string; next: string; due: string; late: boolean };
  problems: Array<{ code: string; path: string; message: string }>;
  folded?: Array<string>;
};

export type LensState = {
  period?: string;
  filters?: Record<string, Array<string>>;
  params?: Record<string, string>;
  select?: Record<string, Array<string>>;
  ranges?: Record<string, NumberRange>;
};

export const isSection = (entry: PageEntry): entry is Section => !Array.isArray(entry) && 'section' in entry;
export const isTabs = (entry: PageEntry): entry is Tabs => !Array.isArray(entry) && 'tabs' in entry;
export const isColumn = (item: RowItem): item is Column => Array.isArray(item);

export const BLOCK_KINDS = lensContract.blockKinds;
export type BlockKind = (typeof BLOCK_KINDS)[number];
export const blockKind = (block: Block): BlockKind | undefined => BLOCK_KINDS.find((kind) => kind in block);

export const isLensDocument = (spec: unknown): spec is LensDocument =>
  Boolean(spec && typeof spec === 'object' && (spec as { version?: unknown }).version === LENS_DOCUMENT_VERSION);

export const asLensDocument = (spec: unknown): LensDocument | undefined =>
  isLensDocument(spec) ? spec : undefined;

function* blocksOf(page: Array<PageEntry>): Generator<Block> {
  for (const entry of page) {
    if (isSection(entry)) for (const row of entry.rows) for (const item of row) yield* isColumn(item) ? item : [item];
    else if (isTabs(entry)) for (const tab of entry.tabs) yield* blocksOf(tab.page);
    else for (const item of entry) yield* isColumn(item) ? item : [item];
  }
}

export function lensCardCount(spec: LensSpec | LensDocument): number {
  return isLensDocument(spec) ? [...blocksOf(spec.page)].length : (spec.cards?.length ?? 0);
}

export function lensPreviewCards(spec: LensSpec | LensDocument): Array<{ type: LensCardType }> {
  return isLensDocument(spec) ? [] : spec.cards;
}
